<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCaptainOperationContext } from 'dashboard/composables/useCaptainOperationContext';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  bulkIds: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['deleteSuccess']);

const { t } = useI18n();
const store = useStore();
const bulkDeleteDialogRef = ref(null);
const i18nKey = computed(() => {
  const i18nTypeMap = {
    AssistantResponse: 'RESPONSES',
    AssistantDocument: 'DOCUMENTS',
  };
  return i18nTypeMap[props.type];
});

const captureContext = useCaptainOperationContext();
const deleting = ref(false);
const deleteError = ref('');
const handleDialogConfirm = async () => {
  const ids = Array.from(props.bulkIds);
  if (deleting.value || !ids.length) return;
  const isCurrent = captureContext();
  const key = i18nKey.value;
  deleting.value = true;
  deleteError.value = '';
  try {
    await store.dispatch('captainBulkActions/handleBulkDelete', {
      ids,
      type: props.type,
    });
    if (!isCurrent()) return;
    emit('deleteSuccess');
    useAlert(t(`CAPTAIN.${key}.BULK_DELETE.SUCCESS_MESSAGE`));
    bulkDeleteDialogRef.value?.close();
  } catch (error) {
    if (isCurrent()) {
      deleteError.value = t(`CAPTAIN.${key}.BULK_DELETE.ERROR_MESSAGE`);
    }
  } finally {
    deleting.value = false;
  }
};
const handleClose = () => {
  if (deleting.value) return;
  deleteError.value = '';
  bulkDeleteDialogRef.value?.close();
};

defineExpose({ dialogRef: bulkDeleteDialogRef });
</script>

<template>
  <Dialog
    ref="bulkDeleteDialogRef"
    type="alert"
    :title="t(`CAPTAIN.${i18nKey}.BULK_DELETE.TITLE`)"
    :description="t(`CAPTAIN.${i18nKey}.BULK_DELETE.DESCRIPTION`)"
    :confirm-button-label="t(`CAPTAIN.${i18nKey}.BULK_DELETE.CONFIRM`)"
    :is-loading="deleting"
    :disable-confirm-button="!bulkIds.size"
    confirm-before-close
    @request-close="handleClose"
    @confirm="handleDialogConfirm"
  >
    <p
      v-if="deleteError"
      role="alert"
      class="rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
    >
      {{ deleteError }}
    </p>
  </Dialog>
</template>
